import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, X, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  name: '',
  discount_type: 'promo_code',
  code: '',
  percent_off: 10,
  max_uses: '',
  per_user_limit: '',
  min_quantity: '',
  expires_at: '',
  is_active: true,
};

/**
 * Admin-only section rendered inside the EventFormDialog that manages
 * discounts for a single event (promo codes + a pre-applied discount).
 *
 * Props:
 *   eventId — the saved event id (null for brand-new events — section hides)
 */
export default function DiscountsSection({ eventId }) {
  const { token } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // discount being edited or null for new
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchDiscounts = useCallback(async () => {
    if (!eventId || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/discounts`, { headers: authHeaders });
      setDiscounts(res.data || []);
    } catch (err) {
      console.warn('Failed to fetch discounts', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '',
      discount_type: d.discount_type,
      code: d.code || '',
      percent_off: d.percent_off,
      max_uses: d.max_uses ?? '',
      per_user_limit: d.per_user_limit ?? '',
      min_quantity: d.min_quantity ?? '',
      expires_at: d.expires_at || '',
      is_active: !!d.is_active,
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Give the discount a name');
      return;
    }
    if (form.discount_type === 'promo_code' && !form.code.trim()) {
      toast.error('Promo codes need a code (e.g. EARLY20)');
      return;
    }
    const pct = Number(form.percent_off);
    if (!(pct > 0 && pct <= 100)) {
      toast.error('Percent off must be between 1 and 100');
      return;
    }
    const payload = {
      name: form.name.trim(),
      discount_type: form.discount_type,
      code: form.discount_type === 'promo_code' ? form.code.trim().toUpperCase() : null,
      percent_off: pct,
      max_uses: form.max_uses === '' ? null : Math.max(0, Number(form.max_uses)),
      per_user_limit: form.per_user_limit === '' ? null : Math.max(0, Number(form.per_user_limit)),
      min_quantity: form.min_quantity === '' ? null : Math.max(1, Number(form.min_quantity)),
      expires_at: form.expires_at || null,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/discounts/${editing.id}`, payload, { headers: authHeaders });
        toast.success('Discount updated');
      } else {
        await axios.post(`${API}/events/${eventId}/discounts`, payload, { headers: authHeaders });
        toast.success('Discount created');
      }
      setOpen(false);
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount? Existing paid orders are unaffected.')) return;
    try {
      await axios.delete(`${API}/discounts/${id}`, { headers: authHeaders });
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!eventId) {
    return (
      <div className="border-t border-zinc-800 pt-6">
        <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">DISCOUNTS</label>
        <p className="text-zinc-500 text-xs">Save the event first to add promo codes or a pre-applied discount.</p>
      </div>
    );
  }

  return (
    <div className="border-t border-zinc-800 pt-6" data-testid="event-discounts-section">
      <div className="flex items-center justify-between mb-4">
        <label className="font-mono text-xs text-zinc-400 tracking-widest">DISCOUNTS</label>
        <button
          type="button"
          onClick={openCreate}
          className="text-[#B5FF00] font-mono text-xs flex items-center gap-1 hover:underline"
          data-testid="add-discount-btn"
        >
          <Plus size={14} /> ADD DISCOUNT
        </button>
      </div>

      {loading && (
        <div className="text-zinc-500 text-xs font-mono flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> LOADING…
        </div>
      )}

      {!loading && discounts.length === 0 && (
        <p className="text-zinc-500 text-xs">
          No discounts yet. Add a <span className="text-[#B5FF00]">PRE-APPLIED</span> discount that auto-applies to every ticket, or a <span className="text-[#B5FF00]">PROMO CODE</span> buyers can type at checkout.
        </p>
      )}

      {!loading && discounts.length > 0 && (
        <div className="space-y-2" data-testid="discounts-list">
          {discounts.map((d) => (
            <div
              key={d.id}
              className="bg-[#050505] border border-zinc-800 p-3 flex items-center justify-between gap-3"
              data-testid={`discount-row-${d.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-['Josefin_Sans']">{d.name}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-mono tracking-wider border ${
                    d.discount_type === 'preapplied'
                      ? 'bg-[#B5FF00]/10 text-[#B5FF00] border-[#B5FF00]/30'
                      : 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                  }`}>
                    {d.discount_type === 'preapplied' ? 'PRE-APPLIED' : 'PROMO CODE'}
                  </span>
                  {!d.is_active && (
                    <span className="px-1.5 py-0.5 text-[10px] font-mono tracking-wider bg-zinc-800 text-zinc-500 border border-zinc-700">
                      INACTIVE
                    </span>
                  )}
                </div>
                <div className="text-zinc-500 text-xs font-mono mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{d.percent_off}% OFF</span>
                  {d.code && <span>CODE: <span className="text-zinc-300">{d.code}</span></span>}
                  <span>USED: {d.used_count}{d.max_uses ? ` / ${d.max_uses}` : ''}</span>
                  {d.min_quantity && <span>MIN {d.min_quantity} TICKETS</span>}
                  {d.per_user_limit && <span>MAX {d.per_user_limit}/USER</span>}
                  {d.expires_at && <span>EXPIRES {String(d.expires_at).slice(0, 16).replace('T', ' ')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(d)}
                  className="text-zinc-500 hover:text-[#B5FF00]"
                  data-testid={`edit-discount-${d.id}`}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(d.id)}
                  className="text-zinc-500 hover:text-red-500"
                  data-testid={`delete-discount-${d.id}`}
                  title="Delete"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6" data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-xl">
              {editing ? 'EDIT DISCOUNT' : 'NEW DISCOUNT'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">TYPE</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'preapplied', title: 'PRE-APPLIED', sub: 'Auto, no code' },
                  { v: 'promo_code', title: 'PROMO CODE', sub: 'Buyer types code' },
                ].map((opt) => {
                  const selected = form.discount_type === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => !editing && setForm({ ...form, discount_type: opt.v })}
                      disabled={!!editing}
                      className={`p-3 border text-left ${
                        selected
                          ? 'border-[#B5FF00] bg-[#B5FF00]/5'
                          : 'border-zinc-800 bg-[#050505] hover:border-zinc-700'
                      } ${editing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`discount-type-${opt.v}`}
                    >
                      <div className="font-mono text-xs text-white tracking-wider">{opt.title}</div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">NAME</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                placeholder="Early-bird 20% off"
                required
                data-testid="discount-name-input"
              />
            </div>

            {form.discount_type === 'promo_code' && (
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">CODE</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm font-mono focus:border-[#B5FF00] focus:outline-none"
                  placeholder="EARLY20"
                  data-testid="discount-code-input"
                />
                <p className="text-zinc-500 text-[11px] mt-1">Buyers type this at checkout. Stored uppercase, case-insensitive match.</p>
              </div>
            )}

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">PERCENT OFF</label>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={form.percent_off}
                onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
                className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="discount-percent-input"
              />
              <p className="text-zinc-500 text-[11px] mt-1">Applied to the ticket subtotal before platform fee + GST.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">MAX USES</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="discount-max-uses-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">PER-USER LIMIT</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.per_user_limit}
                  onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="discount-per-user-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">MIN TICKETS</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.min_quantity}
                  onChange={(e) => setForm({ ...form, min_quantity: e.target.value })}
                  placeholder="None"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="discount-min-qty-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-1 block">EXPIRES AT</label>
                <input
                  type="datetime-local"
                  value={form.expires_at ? form.expires_at.slice(0, 16) : ''}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value ? `${e.target.value}:00` : '' })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="discount-expires-input"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer" data-testid="discount-active-label">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="accent-[#B5FF00] w-4 h-4"
                data-testid="discount-active-checkbox"
              />
              <span className="font-mono text-xs text-zinc-300 tracking-wide">ACTIVE · Can be redeemed by buyers right now</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-secondary flex-1"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-discount-btn"
              >
                {saving ? <><Loader2 size={16} className="animate-spin" /> SAVING…</> : (editing ? 'UPDATE' : 'CREATE')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
